import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "../auth";
import { Link } from "react-router-dom";
import { TrendingUp, MapPin, Calendar, Package, ArrowUpRight, Plus } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell } from "recharts";
import { parseLocalDate } from "../utils/date";

export default function Dashboard() {
  const { user, authHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const h = { headers: authHeaders() };
        const [s, r] = await Promise.all([
          axios.get(`${API}/reports/stats`, h),
          axios.get(`${API}/reports`, h),
        ]);
        setStats(s.data);
        setRecent(r.data.slice(0, 5));
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Buenos días";
    if (h < 19) return "Buenas tardes";
    return "Buenas noches";
  };

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="kbd-label mb-2 text-emerald-700">/ Panel principal</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
            {greeting()}, <span className="text-slate-500">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-slate-500 mt-2">Resumen de actividad de visitas en campo.</p>
        </div>
        <Link
          to="/reports/new"
          className="inline-flex items-center gap-2 bg-emerald-700 text-white px-5 py-3 rounded-md hover:bg-emerald-800 transition-colors font-medium self-start md:self-auto"
          data-testid="new-report-cta"
        >
          <Plus className="w-4 h-4" /> Nuevo reporte
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-10">
        <StatCard label="Hoy" value={stats?.today ?? 0} icon={Calendar} accent="emerald" testId="stat-today" />
        <StatCard label="Esta semana" value={stats?.week ?? 0} icon={TrendingUp} accent="slate" testId="stat-week" />
        <StatCard label="Este mes" value={stats?.month ?? 0} icon={Package} accent="slate" testId="stat-month" />
        <StatCard label="Total" value={stats?.total ?? 0} icon={MapPin} accent="slate" testId="stat-total" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5 md:p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="kbd-label text-emerald-700">/ Últimos 7 días</div>
              <h3 className="font-display text-xl font-bold text-slate-950 mt-1">Actividad reciente</h3>
            </div>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.trend ?? []}>
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => parseLocalDate(d).toLocaleDateString("es", { weekday: "short" })}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  stroke="#94a3b8"
                />
                <Tooltip
                  cursor={{ fill: "#ecfdf5" }}
                  contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }}
                  labelFormatter={(d) => parseLocalDate(d).toLocaleDateString("es")}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {(stats?.trend ?? []).map((d, i) => (
                    <Cell key={d.date} fill={i === (stats?.trend?.length ?? 0) - 1 ? "#059669" : "#a7f3d0"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5 md:p-6">
          <div className="kbd-label text-emerald-700">/ Top marcas</div>
          <h3 className="font-display text-xl font-bold text-slate-950 mt-1 mb-5">Por volumen</h3>
          {stats?.by_brand?.length ? (
            <ul className="space-y-3">
              {stats.by_brand.map((b) => {
                const max = Math.max(...stats.by_brand.map((x) => x.count));
                const pct = max ? (b.count / max) * 100 : 0;
                return (
                  <li key={b.brand} className="tv-ticker-line pl-4">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-medium text-slate-900 truncate">{b.brand}</span>
                      <span className="tv-stat-value text-lg text-slate-950">{b.count}</span>
                    </div>
                    <div className="mt-1.5 h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-600" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">Sin datos aún.</p>
          )}
        </div>
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-5 md:p-6 border-b border-slate-200">
          <div>
            <div className="kbd-label text-emerald-700">/ Últimos reportes</div>
            <h3 className="font-display text-xl font-bold text-slate-950 mt-1">Actividad más reciente</h3>
          </div>
          <Link to="/reports" className="text-sm font-medium text-emerald-700 hover:underline flex items-center gap-1" data-testid="view-all-reports">
            Ver todo <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <p className="font-display text-xl text-slate-900 mb-2">Aún no hay reportes</p>
            <p className="text-sm">Crea tu primer reporte de visita para empezar.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recent.map((r) => (
              <li key={r.id}>
                <Link to={`/reports/${r.id}`} className="flex items-center gap-4 p-4 md:px-6 hover:bg-slate-50 transition-colors" data-testid={`recent-report-${r.id}`}>
                  <div className="w-12 h-12 shrink-0 bg-emerald-50 border border-emerald-200 flex flex-col items-center justify-center rounded-md">
                    <span className="text-[10px] text-emerald-800 leading-none font-semibold">
                      {parseLocalDate(r.fecha).toLocaleDateString("es", { month: "short" }).toUpperCase()}
                    </span>
                    <span className="font-display font-bold text-base leading-tight text-emerald-900">
                      {parseLocalDate(r.fecha).getDate()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-950">{r.brand}</span>
                      <span className="kbd-label !text-[9px]">· {r.chain}</span>
                    </div>
                    <div className="text-sm text-slate-500 truncate">
                      {r.branch} · {r.city} · {r.visitor_name}{r.visitor_position && ` (${r.visitor_position})`}
                    </div>
                  </div>
                  <div className="hidden md:block text-xs text-slate-400">
                    {r.photos?.length || 0} fotos
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, testId }) {
  const accents = { emerald: "text-emerald-700", slate: "text-slate-500" };
  return (
    <div className="bg-white border border-slate-200 p-4 md:p-5 rounded-lg hover:border-emerald-400 transition-colors" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <span className="kbd-label">{label}</span>
        <Icon className={`w-4 h-4 ${accents[accent]}`} />
      </div>
      <div className="tv-stat-value text-4xl md:text-5xl text-slate-950">{value}</div>
    </div>
  );
}
